import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { ImageUpload } from "@/components/ui/image-upload";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Eye, EyeOff } from "lucide-react";

interface UserProfile {
  id: string;
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
  email: string | null;
  role: string;
  suspended: boolean;
  last_login: string | null;
  assigned_clients: string[] | null;
  assigned_projects: string[] | null;
  profile_image_url: string | null;
  created_at: string;
  manager_id: string | null;
  cost_per_hour: number | null;
  manager?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    full_name: string | null;
  } | null;
}

interface UserFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: "add" | "edit";
  user?: UserProfile | null;
  users: UserProfile[];
  onUserCreated: () => void;
  onUserUpdated: () => void;
}

export function UserFormModal({
  isOpen,
  onClose,
  mode,
  user,
  users,
  onUserCreated,
  onUserUpdated
}: UserFormModalProps) {
  const { toast } = useToast();
  
  console.log("UserFormModal rendered", { isOpen, mode, user, usersCount: users.length });
  
  const [formData, setFormData] = useState({
    firstName: user?.first_name || "",
    lastName: user?.last_name || "",
    email: user?.email || "",
    password: "",
    profileImageUrl: user?.profile_image_url || "",
    managerId: user?.manager_id || "none",
    costPerHour: user?.cost_per_hour?.toString() || ""
  });

  const [showPassword, setShowPassword] = useState(false);

  // Email validation function with internationalized domain support
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return false;
    
    // Check for non-ASCII characters in domain part
    const [localPart, domain] = email.split('@');
    if (domain && /[^\x00-\x7F]/.test(domain)) {
      // Has non-ASCII characters - needs punycode conversion
      return false;
    }
    
    return true;
  };

  // Helper function to get validation error message
  const getEmailValidationError = (email: string) => {
    if (!email) return "Email is required";
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return "Please enter a valid email address";
    
    const [localPart, domain] = email.split('@');
    if (domain && /[^\x00-\x7F]/.test(domain)) {
      return "Domain names with non-English characters are not supported. Please use an ASCII domain (e.g., braincandy.gr instead of βραινψανδυ.γρ)";
    }
    
    return null;
  };

  // Check if email already exists
  const checkEmailExists = (email: string, excludeUserId?: string) => {
    return users.some(u => 
      u.email?.toLowerCase() === email.toLowerCase() && 
      u.user_id !== excludeUserId
    );
  };

  const handleAddUser = async () => {
    // Validation
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      toast({
        title: "Validation Error",
        description: "First name and last name are required",
        variant: "destructive"
      });
      return;
    }

    const emailError = getEmailValidationError(formData.email);
    if (emailError) {
      toast({
        title: "Validation Error", 
        description: emailError,
        variant: "destructive"
      });
      return;
    }

    if (checkEmailExists(formData.email)) {
      toast({
        title: "Validation Error",
        description: "A user with this email already exists",
        variant: "destructive"
      });
      return;
    }

    if (formData.password.length < 6) {
      toast({
        title: "Validation Error",
        description: "Password must be at least 6 characters long",
        variant: "destructive"
      });
      return;
    }

    try {
      // Call the edge function to create a real authenticated user
      const { data, error } = await supabase.functions.invoke('create-user', {
        body: {
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          role: 'user', // Default role
          managerId: formData.managerId === "none" ? null : formData.managerId || null,
          costPerHour: formData.costPerHour ? parseFloat(formData.costPerHour) : null
        }
      });

      if (error) {
        throw error;
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to create user');
      }

      toast({
        title: "User created successfully",
        description: `User ${formData.email} has been created and can now log in.`,
      });
      
      onClose();
      resetForm();
      onUserCreated();
    } catch (error: any) {
      toast({
        title: "Error creating user",
        description: error.message || "Failed to create user",
        variant: "destructive"
      });
    }
  };

  const handleEditUser = async () => {
    console.log("handleEditUser called", { user, formData });
    if (!user) return;

    // Validation
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      console.log("Validation failed - missing names", { firstName: formData.firstName, lastName: formData.lastName });
      toast({
        title: "Validation Error",
        description: "First name and last name are required",
        variant: "destructive"
      });
      return;
    }

    const emailError = getEmailValidationError(formData.email);
    if (emailError) {
      toast({
        title: "Validation Error",
        description: emailError, 
        variant: "destructive"
      });
      return;
    }

    if (checkEmailExists(formData.email, user.user_id)) {
      toast({
        title: "Validation Error",
        description: "A user with this email already exists",
        variant: "destructive"
      });
      return;
    }

    // Validate password if provided
    if (formData.password && formData.password.length < 6) {
      toast({
        title: "Validation Error",
        description: "Password must be at least 6 characters long",
        variant: "destructive"
      });
      return;
    }

    // Validate cost per hour if provided
    if (formData.costPerHour && (isNaN(parseFloat(formData.costPerHour)) || parseFloat(formData.costPerHour) < 0)) {
      toast({
        title: "Validation Error",
        description: "Cost per hour must be a valid positive number",
        variant: "destructive"
      });
      return;
    }

    console.log("Starting update operation", { 
      user_id: user.user_id, 
      updateData: {
        first_name: formData.firstName,
        last_name: formData.lastName,
        full_name: `${formData.firstName} ${formData.lastName}`.trim(),
        email: formData.email,
        profile_image_url: formData.profileImageUrl,
        cost_per_hour: formData.costPerHour ? parseFloat(formData.costPerHour) : null,
        hasPasswordUpdate: !!formData.password
      }
    });

    let profileUpdateSuccess = false;
    let passwordUpdateSuccess = false;

    try {
      // Update password FIRST if provided
      if (formData.password) {
        console.log("Updating password first...");
        
        const { data, error: passwordError } = await supabase.functions.invoke('update-user-password', {
          body: {
            userId: user.user_id,
            newPassword: formData.password
          }
        });

        if (passwordError) {
          console.error("Password update error:", passwordError);
          throw new Error(`Password update failed: ${passwordError.message}`);
        }

        if (!data?.success) {
          console.error("Password update returned failure:", data);
          throw new Error(data?.error || 'Failed to update password');
        }

        console.log("Password updated successfully");
        passwordUpdateSuccess = true;
      }

      // Then update profile data with retry logic
      console.log("Starting profile update for user:", user.user_id);
      
      let profileError;
      let retryCount = 0;
      const maxRetries = 3;
      
      while (retryCount < maxRetries) {
        const { error } = await supabase
          .from('profiles')
          .update({
            first_name: formData.firstName,
            last_name: formData.lastName,
            full_name: `${formData.firstName} ${formData.lastName}`.trim(),
            email: formData.email,
            profile_image_url: formData.profileImageUrl,
            manager_id: formData.managerId === "none" ? null : formData.managerId || null,
            cost_per_hour: formData.costPerHour ? parseFloat(formData.costPerHour) : null
          })
          .eq('user_id', user.user_id);

        if (!error) {
          profileError = null;
          profileUpdateSuccess = true;
          break;
        }
        
        profileError = error;
        retryCount++;
        console.log(`Profile update attempt ${retryCount} failed:`, error.message);
        
        if (retryCount < maxRetries) {
          // Wait before retry
          await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
        }
      }

      if (profileError) {
        console.error("Profile update failed after all retries:", profileError);
        
        // If password was updated but profile failed, still show partial success
        if (passwordUpdateSuccess) {
          toast({
            title: "Partial update successful",
            description: "Password was updated successfully, but profile update failed. Please try updating the profile information again.",
            variant: "destructive"
          });
          onClose();
          onUserUpdated();
          return;
        } else {
          throw new Error(`Failed to update profile: ${profileError.message}`);
        }
      }

      console.log("Profile updated successfully");
      console.log("Update completed successfully");

      toast({
        title: "User updated successfully",
        description: `User ${formData.email} has been updated${formData.password ? ' (including password)' : ''}.`,
      });
      
      onClose();
      onUserUpdated();
    } catch (error: any) {
      console.error("Update failed:", error);
      
      let errorMessage = error.message || "Failed to update user";
      
      // Add context about what succeeded/failed
      if (passwordUpdateSuccess && !profileUpdateSuccess) {
        errorMessage = "Password was updated but profile update failed: " + errorMessage;
      }
      
      toast({
        title: "Error updating user",
        description: errorMessage,
        variant: "destructive"
      });
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      profileImageUrl: "",
      managerId: "none",
      costPerHour: ""
    });
  };

  const handleSubmit = () => {
    console.log("handleSubmit called", { mode, user });
    if (mode === "add") {
      handleAddUser();
    } else {
      console.log("Calling handleEditUser");
      handleEditUser();
    }
  };

  const handleClose = () => {
    onClose();
    if (mode === "add") {
      resetForm();
    }
  };

  // Update form data when user prop changes (useEffect instead of inline)
  useEffect(() => {
    if (mode === "edit" && user) {
      setFormData({
        firstName: user.first_name || "",
        lastName: user.last_name || "",
        email: user.email || "",
        password: "",
        profileImageUrl: user.profile_image_url || "",
        managerId: user.manager_id || "none",
        costPerHour: user.cost_per_hour?.toString() || ""
      });
    }
  }, [mode, user]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{mode === "add" ? "Add New User" : "Edit User"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="firstName">First Name</Label>
              <Input 
                value={formData.firstName}
                onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                placeholder="John"
              />
            </div>
            <div>
              <Label htmlFor="lastName">Last Name</Label>
              <Input 
                value={formData.lastName}
                onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                placeholder="Doe"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="email">Email</Label>
            <Input 
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              placeholder="john@company.com"
            />
          </div>

          <div>
            <Label htmlFor="costPerHour">Cost per hour (€)</Label>
            <Input 
              type="number"
              step="0.01"
              min="0"
              value={formData.costPerHour}
              onChange={(e) => setFormData({...formData, costPerHour: e.target.value})}
              placeholder="50.00"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Hourly rate in euros (optional)
            </p>
          </div>

          <div>
            <Label>Profile Picture</Label>
            <ImageUpload
              value={formData.profileImageUrl}
              onChange={(url) => setFormData({...formData, profileImageUrl: url})}
              bucket="user-avatars"
            />
          </div>

          {/* Direct Report field - only show for non-Super Admin users */}
          {!(user?.email?.toLowerCase().includes('stamatis') || user?.first_name?.toLowerCase() === 'stamatis') && (
            <div>
              <Label htmlFor="manager">Direct Report (Manager)</Label>
              <Select 
                value={formData.managerId} 
                onValueChange={(value) => setFormData({...formData, managerId: value})}
              >
                <SelectTrigger className="bg-background">
                  <SelectValue placeholder="Select manager" />
                </SelectTrigger>
                <SelectContent className="bg-background z-50">
                  <SelectItem value="none">No Manager</SelectItem>
                  {(() => {
                    const availableManagers = users.filter(u => 
                      // Don't include the current user being edited
                      u.id !== user?.id
                    );
                    console.log("Available managers for dropdown:", availableManagers);
                    return availableManagers.map((manager) => (
                      <SelectItem key={manager.id} value={manager.id}>
                        {manager.full_name || `${manager.first_name || ''} ${manager.last_name || ''}`.trim() || manager.email}
                      </SelectItem>
                    ));
                  })()}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Select a manager for this user.
              </p>
            </div>
          )}

          <div>
            <Label htmlFor="password">
              {mode === "add" ? "Password" : "New Password"}
            </Label>
            <div className="relative">
              <Input 
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                placeholder={mode === "add" ? "Minimum 6 characters" : "Enter new password"}
                className="pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Eye className="h-4 w-4 text-muted-foreground" />
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {mode === "add" 
                ? "Password must be at least 6 characters long"
                : `Current password: ${'*'.repeat(12)} - Enter new password to change (min 6 characters)`
              }
            </p>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleSubmit} className="flex-1">
              {mode === "add" ? "Create User" : "Update User"}
            </Button>
            <Button 
              variant="outline" 
              onClick={handleClose}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}